import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';

interface SignaturePadProps {
  onChange: (pngDataUrl: string | null) => void;
  height?: number;
  disabled?: boolean;
}

const MAX_PIXEL_RATIO = 2;

const SignaturePad: React.FC<SignaturePadProps> = ({ onChange, height = 160, disabled = false }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const [hasInk, setHasInk] = useState(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
    const width = canvas.parentElement?.clientWidth || 400;
    canvas.width = Math.floor(width * ratio);
    canvas.height = Math.floor(height * ratio);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111';
    setHasInk(false);
    onChangeRef.current(null);
  }, [height]);

  useEffect(() => {
    resizeCanvas();
    // Resizing clears the canvas, so only react to real width changes (not mobile scroll bars).
    let lastWidth = canvasRef.current?.parentElement?.clientWidth;
    const handleResize = () => {
      const width = canvasRef.current?.parentElement?.clientWidth;
      if (width && width !== lastWidth) {
        lastWidth = width;
        resizeCanvas();
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [resizeCanvas]);

  const pointFromEvent = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    const point = pointFromEvent(e);
    lastPointRef.current = point;
    const ctx = e.currentTarget.getContext('2d');
    if (ctx) {
      ctx.beginPath();
      ctx.arc(point.x, point.y, 1.2, 0, Math.PI * 2);
      ctx.fillStyle = '#111';
      ctx.fill();
    }
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current || disabled) return;
    e.preventDefault();
    const ctx = e.currentTarget.getContext('2d');
    const last = lastPointRef.current;
    const point = pointFromEvent(e);
    if (ctx && last) {
      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(point.x, point.y);
      ctx.stroke();
    }
    lastPointRef.current = point;
    if (!hasInk) setHasInk(true);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    lastPointRef.current = null;
    setHasInk(true);
    onChangeRef.current(e.currentTarget.toDataURL('image/png'));
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) {
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.restore();
    }
    setHasInk(false);
    onChangeRef.current(null);
  };

  return (
    <Box>
      <Box
        sx={{
          position: 'relative',
          border: '1px dashed',
          borderColor: 'grey.400',
          borderRadius: 1,
          bgcolor: disabled ? 'grey.100' : '#fff',
          overflow: 'hidden',
        }}
      >
        <canvas
          ref={canvasRef}
          aria-label="Signature pad"
          style={{ display: 'block', touchAction: 'none', cursor: disabled ? 'default' : 'crosshair' }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        />
        {!hasInk && (
          <Typography
            variant="body2"
            color="text.disabled"
            sx={{ position: 'absolute', left: 16, bottom: 12, pointerEvents: 'none' }}
          >
            Sign here with your finger or mouse
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
        <Button size="small" onClick={handleClear} disabled={disabled || !hasInk}>
          Clear
        </Button>
      </Box>
    </Box>
  );
};

export default SignaturePad;
